declare global {
  interface Window {
    dynamicEnvConfig?: Partial<Config>;
  }
}

export interface Config {
  // Build-time git SHA if baked in, else window.dynamicEnvConfig.version (see below).
  version: string;
  clientId: string;
  sentryDsn: string;
  fullStoryOrgId: string;
  production: boolean;
  environment: string;
  authServiceBaseUrl: string;
  defaultRedirectUrl: string;
  errorRedirectUrl: string;
  logoutRedirectAllowedBaseUrls: string[];
  consoleLogsEnabled: boolean;
  dataDogEnabled: boolean;
  resendMFACooldownSeconds: number;
  dataDog: {
    appId: string;
    clientToken: string;
    site: string;
    service: string;
  };
  analytics: {
    enabled: boolean;
    printEvents: boolean;
    segmentKey: string;
  };
}

const platformAppBaseUrl = import.meta.env.VITE_PLATFORM_APP_URL || '';
const developersPortalLocalBaseUrl = 'https://localhost:1212';
const APP_MELIO_COM_ENVIRONMENTS = ['staging01', 'eilat'];

const defaultConfig: Config = {
  environment: 'development',
  version: '1.0.0',
  production: false,
  sentryDsn: '',
  fullStoryOrgId: '10BZN1',
  authServiceBaseUrl: '/v1', // CloudFront proxies to auth-web
  defaultRedirectUrl: `${platformAppBaseUrl}/start`,
  errorRedirectUrl: `${platformAppBaseUrl}/error`,
  logoutRedirectAllowedBaseUrls: [platformAppBaseUrl],
  consoleLogsEnabled: true,
  resendMFACooldownSeconds: 30,
  clientId: 'melio:auth:app:cbbe566899c5d74bd40ab017326a4dbccc879379',
  dataDogEnabled: false,
  dataDog: {
    appId: '85183b18-2885-44e7-b7dc-eb496459de87',
    clientToken: 'pubb5e21ec65efdfd84fdeb0bff1f9b1283',
    site: 'datadoghq.com',
    service: 'auth-app',
  },
  analytics: {
    enabled: false,
    printEvents: true,
    segmentKey: 'a0nAg8QHdKU4IkCiyrl4zIjRQAyL6NEB', // https://app.segment.com/melio/sources/melio_web_demo
  },
};

const getEnvConfig = (env: string): Partial<Config> => {
  // Development - use default
  if (env === 'development') {
    return {};
  }

  // Production
  if (env === 'production') {
    return {
      defaultRedirectUrl: 'https://app.melio.com/start',
      errorRedirectUrl: 'https://app.melio.com/error',
      logoutRedirectAllowedBaseUrls: ['https://app.melio.com', 'https://developers.melio.com'],
      dataDogEnabled: true,
      consoleLogsEnabled: false,
      analytics: {
        enabled: true,
        printEvents: false,
        segmentKey: 'szPSLPsn0eLI4th6z5pTk91AbgVHmxg4', // Production key (same as platform-app)
      },
    };
  }

  // Non-prod environments (eilat, staging01, int02, etc.)
  const redirectBaseUrl = APP_MELIO_COM_ENVIRONMENTS.includes(env)
    ? `https://app.${env}.melio.com`
    : `https://partnerships.${env}.melioservices.com/melio`;
  return {
    defaultRedirectUrl: `${redirectBaseUrl}/start`,
    errorRedirectUrl: `${redirectBaseUrl}/error`,
    logoutRedirectAllowedBaseUrls: [
      `https://app.${env}.melio.com`,
      `https://partnerships.${env}.melioservices.com/melio`,
      `https://partnerships-stable.${env}.melioservices.com/melio`,
      `https://developers.${env}.melio.com`,
      developersPortalLocalBaseUrl,
    ],
    dataDogEnabled: true,
    analytics: {
      enabled: true,
      printEvents: true,
      segmentKey: 'ktaEnCSilTDj4NuZ1Ny6rINi0rnZsAK4', // Staging key (same as platform-app)
    },
  };
};

const baseConfig: Config = { ...defaultConfig, ...window.dynamicEnvConfig };
const envConfig = getEnvConfig(baseConfig.environment);

export const config: Config = {
  ...baseConfig,
  ...envConfig,
  // Prefer the build's own version so a stale cached bundle isn't tagged with the server's.
  version: import.meta.env.VITE_GIT_SHA || baseConfig.version,
};
